/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/no-unstable-nested-components */
import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import moment from 'moment-timezone';
import { CSVLink } from 'react-csv';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  Clock, Timer, TrendingUp, ChevronRight, Download, ArrowLeft,
} from 'lucide-react';
import './reports.css';
import { Navbar, Sidebar } from '../../components';
import DateRangePicker from '../../components/dateRangePicker';
import FilterModal from './FilterModal';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import { sendRequest } from '../../api/shootAPI';
import { getDatesInRange, convertSecToTimeFormat } from '../../utils/time';
import {
  getTotalHours, getManualHours, generateActivityReport, transformToTableData, exportTableData,
} from '../../utils/report';
import API from '../../api/endpoints';

const softShadow = 'shadow-[0_1px_2px_rgba(14,30,64,0.04)]';

const ACTIVITIES = [
  { id: 'deep', label: 'Deep Focused', letter: 'D', color: '#213D69' },
  { id: 'focused', label: 'Focused', letter: 'F', color: '#5A81BC' },
  { id: 'active', label: 'Active', letter: 'A', color: '#B5BAC4' },
  { id: 'break', label: 'Work Break', letter: 'W', color: '#F19235', noScore: true },
  { id: 'manual', label: 'Manual Hours', letter: 'M', color: '#265eb8', noScore: true },
];

const csvHeaders = [
  { label: 'Date', key: 'date' },
  { label: 'Total Hours', key: 'totalSeconds' },
  { label: 'Total Activity', key: 'totalActivity' },
  { label: 'Deep Focused Hours', key: 'deepFocused' },
  { label: 'Deep Focused Activity', key: 'deepFocusedActivity' },
  { label: 'Focused Hours', key: 'focused' },
  { label: 'Focused Activity', key: 'focusedActivity' },
  { label: 'Active Hours', key: 'active' },
  { label: 'Active Activity', key: 'activeActivity' },
  { label: 'Work Break Hours', key: 'workBreak' },
];

function engagementColor(value) {
  if (value >= 70) return '#53A551';
  if (value >= 40) return '#F19235';
  return '#b94f3c';
}

function KPI({
  icon: Icon, label, value, accent,
}) {
  return (
    <div className={`rounded-xl bg-white border border-surface-2 ${softShadow} p-4`}>
      <p className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-3">{label}</p>
      <div className="flex items-center justify-between">
        <span
          className="text-3xl font-bold tabular-nums leading-none"
          style={{ color: accent }}
        >
          {value}
        </span>
        <span
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${accent}1F`, color: accent }}
        >
          <Icon size={18} />
        </span>
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const order = ['Deep Focused', 'Focused', 'Active', 'Work Break'];
  const colors = {
    'Deep Focused': '#213D69',
    Focused: '#5A81BC',
    Active: '#B5BAC4',
    'Work Break': '#F19235',
  };
  const map = new Map();
  payload.forEach((p) => map.set(p.name, p.value));
  return (
    <div className="rounded-xl bg-white border border-surface-2 shadow-[0_8px_24px_rgba(14,30,64,0.08)] px-4 py-3 min-w-[200px]">
      <p className="text-sm font-semibold text-[#0e1e40] mb-2">{label}</p>
      <div className="space-y-1.5">
        {order.map((name) => {
          const value = map.get(name) ?? 0;
          return (
            <div key={name} className="flex items-center text-xs">
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0 mr-2"
                style={{ backgroundColor: colors[name] }}
              />
              <span className="text-ink-900 flex-1">{name}</span>
              <span className="text-ink-900 tabular-nums ml-6">
                {convertSecToTimeFormat(Math.round(value * 3600))}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActivityBadge({ activityId, size = 28 }) {
  const a = ACTIVITIES.find((x) => x.id === activityId);
  return (
    <div
      className="rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
      style={{
        width: size,
        height: size,
        backgroundColor: a?.color ?? '#B5BAC4',
      }}
    >
      {a?.letter ?? '?'}
    </div>
  );
}

function DayRow({ day, isOpen, onToggle }) {
  const workingSec = Math.max((day.totalSeconds || 0) - (day.workBreak || 0), 0);
  const activityRows = [
    { id: 'deep', duration: day.deepFocused },
    { id: 'focused', duration: day.focused },
    { id: 'active', duration: day.active },
    { id: 'break', duration: day.workBreak },
    { id: 'manual', duration: day.manualSeconds },
  ];

  return (
    <div>
      <div className="px-5 py-2 bg-surface-page text-[11px] uppercase tracking-wider text-ink-500 font-semibold">
        {day.date}
      </div>
      <button
        type="button"
        onClick={onToggle}
        className="w-full grid grid-cols-[1fr_180px_180px] items-center px-5 py-3 text-sm hover:bg-surface-page transition"
      >
        <span className="flex items-center gap-2 text-ink-900 font-medium">
          <ChevronRight
            size={14}
            className={`text-ink-500 transition-transform ${isOpen ? 'rotate-90' : ''}`}
          />
          Total
        </span>
        <span className="text-right text-ink-900 tabular-nums">
          {convertSecToTimeFormat(day.totalSeconds)}
        </span>
        <span className="text-right pr-2 text-ink-900 tabular-nums">
          {day.totalSeconds > 0 ? `${day.totalActivity}%` : '0%'}
        </span>
      </button>
      {isOpen && activityRows.map((a) => {
        const meta = ACTIVITIES.find((x) => x.id === a.id);
        const score = meta.noScore || workingSec === 0
          ? '—'
          : `${Math.round(((a.duration || 0) / workingSec) * 100)}%`;
        return (
          <div
            key={a.id}
            className="grid grid-cols-[1fr_180px_180px] items-center px-5 py-3 text-sm border-t border-surface-1"
          >
            <span className="flex items-center gap-3 text-ink-900">
              <ActivityBadge activityId={a.id} />
              {meta.label}
            </span>
            <span className="text-right text-ink-900 tabular-nums">
              {convertSecToTimeFormat(a.duration || 0)}
            </span>
            <span className="text-right pr-2 text-ink-500 tabular-nums">
              {score}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function TimeWorked() {
  const location = useLocation();
  const history = useHistory();
  const routeParams = useParams();
  const queryParams = new URLSearchParams(location.search);
  // memberId arrives via the route param when rendered under the Team
  // Engagement member route, or via ?memberId= as a fallback.
  const initialMemberId = Number(routeParams.memberId) || Number(queryParams.get('memberId')) || 0;
  // Arrived from a member's "View Report" → member-scoped view: back arrow,
  // smaller title, member name, and no filter (matches the prototype).
  const isMemberView = initialMemberId > 0;
  const today = new Date();
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace } = useContext(UserContext);
  const [date, setDate] = useState([new Date(moment(today).subtract(6, 'days')), today]);
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [teamId, setTeamId] = useState(0);
  const [memberId, setMemberId] = useState(initialMemberId);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [qualitySeconds, setQualitySeconds] = useState(0);
  const [manualSeconds, setManualSeconds] = useState(0);
  const [engageScore, setEngageScore] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [displayText, setDisplayText] = useState('All');
  const [openDays, setOpenDays] = useState(new Set());
  const [pending, setPending] = useState({ teams: false, members: false });

  const fetchTeams = async () => {
    const [startDate, endDate] = date;
    const dates = getDatesInRange(startDate, endDate);
    const startOfDay = moment(dates[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const endOfDay = moment(dates.at(-1)).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    setPending((prev) => ({ ...prev, teams: true }));
    const { success, data } = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.createTeam(activeWorkspace.id),
      timeout: 30000,
      params: { start: startOfDay, end: endOfDay, searchQuery: '' },
    });
    if (success) {
      setTeams(data.teams);
    } else {
      displayNotification({
        message: 'There was an issue fetching team data in this workspace. Please try again',
        severity: 'error',
      });
    }
    setPending((prev) => ({ ...prev, teams: false }));
  };

  const fetchMembers = async () => {
    const [startDate, endDate] = date;
    const dates = getDatesInRange(startDate, endDate);
    const startOfDay = moment(dates[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const endOfDay = moment(dates.at(-1)).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    setPending((prev) => ({ ...prev, members: true }));
    const { success, data } = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.membersDailyActivity(activeWorkspace.id),
      params: { start: startOfDay, end: endOfDay },
    });
    if (success) {
      const membersData = data.members.map((member) => ({
        ...member,
        dailyActivity: member.dailyActivityTz,
      }));
      setMembers(membersData);
    } else {
      displayNotification({
        message: 'There was an issue fetching members data in this workspace. Please try again',
        severity: 'error',
      });
    }
    setPending((prev) => ({ ...prev, members: false }));
  };

  const handleSearch = (filterTeamId, filterMemberId) => {
    setTeamId(filterTeamId);
    setMemberId(filterMemberId);
    if (filterTeamId === 0 && filterMemberId === 0) {
      setFilteredMembers(members);
      setDisplayText('All');
    } else if (filterTeamId !== 0) {
      const filteredTeam = teams.find((team) => team.id === filterTeamId);
      setDisplayText(filteredTeam?.name || 'All');
      const filterMembers = [];
      filteredTeam?.teamMates?.forEach((m) => {
        const filterMember = members.find((item) => item.id === m.memberId);
        if (filterMember) filterMembers.push(filterMember);
      });
      setFilteredMembers(filterMembers);
    } else {
      const filterMembers = [];
      const filterMember = members.find((item) => item.id === filterMemberId);
      if (filterMember && filterMember.user) {
        setDisplayText(`${filterMember.user.firstName} ${filterMember.user.lastName}`);
        filterMembers.push(filterMember);
      } else {
        setDisplayText('All');
      }
      setFilteredMembers(filterMembers);
    }
  };

  useEffect(() => {
    fetchTeams();
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    if (teamId === 0 && memberId === 0) {
      setFilteredMembers(members);
    } else if (teamId !== 0) {
      const filteredTeam = teams.find((team) => team.id === teamId);
      const filterMembers = [];
      filteredTeam?.teamMates?.forEach((m) => {
        const filterMember = members.find((item) => item.id === m.memberId);
        if (filterMember) filterMembers.push(filterMember);
      });
      setFilteredMembers(filterMembers);
    } else {
      const filterMembers = [];
      const filterMember = members.find((item) => item.id === memberId);
      if (filterMember) {
        filterMembers.push(filterMember);
        if (filterMember.user) {
          setDisplayText(`${filterMember.user.firstName} ${filterMember.user.lastName}`);
        }
      }
      setFilteredMembers(filterMembers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members]);

  useEffect(() => {
    const { totalSeconds: ts, activeSeconds: as } = getTotalHours(filteredMembers);
    const { totalManualSeconds } = getManualHours(filteredMembers);
    setTotalSeconds(ts);
    setQualitySeconds(as);
    setManualSeconds(totalManualSeconds);
    const workingSec = Math.max(ts, 1);
    setEngageScore(ts > 0 ? Math.round((as * 100) / workingSec) : 0);

    const result = generateActivityReport(filteredMembers, date[0], date[1]);
    const table = transformToTableData(result);
    setTableData(table);
    setExportData(exportTableData(result));

    const firstWithData = table.findIndex((d) => d.totalSeconds > 0);
    setOpenDays(firstWithData >= 0 ? new Set([firstWithData]) : new Set());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredMembers]);

  const chartData = useMemo(
    () => tableData.map((d) => ({
      date: d.date.replace(/^[A-Za-z]+, /, ''),
      'Deep Focused': (d.deepFocused || 0) / 3600,
      Focused: (d.focused || 0) / 3600,
      Active: (d.active || 0) / 3600,
      'Work Break': (d.workBreak || 0) / 3600,
    })),
    [tableData],
  );

  const toggleDay = (idx) => {
    setOpenDays((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  return (
    <>
      <Navbar />
      <Sidebar />
      <div className="screen-wrapper">
        <div className="screen-header">
          {(pending.teams || pending.members) && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255,255,255,0.5)',
                zIndex: 2000,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1 flex items-center gap-2">
              {isMemberView && (
                <button
                  type="button"
                  aria-label="Back"
                  onClick={() => history.goBack()}
                  className="p-1 -ml-1 rounded text-ink-900 hover:bg-gray-100 transition flex-shrink-0"
                >
                  <ArrowLeft size={22} />
                </button>
              )}
              <h1 className={`font-bold text-ink-900 leading-tight truncate ${
                isMemberView ? 'text-xl lg:text-2xl' : 'text-2xl lg:text-[32px] lg:leading-10'
              }`}
              >
                Time Worked:&nbsp;
                {displayText}
              </h1>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:justify-end w-full sm:w-auto" data-tour="report-filters">
              <div className="flex-1 sm:flex-none min-w-0">
                <DateRangePicker
                  value={date}
                  onChange={(_date) => setDate(_date)}
                  className="w-auto"
                />
              </div>
              {!isMemberView && (
                <FilterModal
                  pending={pending}
                  handleSearch={handleSearch}
                  members={members}
                  teams={teams}
                  defaultMemberId={initialMemberId}
                  isActive={teamId !== 0 || memberId !== 0}
                />
              )}
            </div>
          </div>

          <div className="space-y-5">
            {/* KPI strip */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-tour="report-kpis">
              <KPI
                icon={Clock}
                label="Total Hours"
                value={convertSecToTimeFormat(totalSeconds)}
                accent="#53A551"
              />
              <KPI
                icon={Timer}
                label="Manual Hours"
                value={convertSecToTimeFormat(manualSeconds)}
                accent="#3d5a8a"
              />
              <KPI
                icon={Timer}
                label="Quality Hours"
                value={convertSecToTimeFormat(qualitySeconds)}
                accent="#5A81BC"
              />
              <KPI
                icon={TrendingUp}
                label="Engagement Score"
                value={`${engageScore}%`}
                accent={engagementColor(engageScore)}
              />
            </div>

            {/* Chart */}
            <div className={`rounded-xl bg-white border border-surface-2 ${softShadow} p-5`}>
              <p className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-4">
                Total Hours Worked Per Day
              </p>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    barSize={16}
                    margin={{
                      top: 8, right: 8, left: -8, bottom: 0,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e1e6" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#546077' }}
                      axisLine={{ stroke: '#e0e1e6' }}
                      tickLine={false}
                      interval="preserveStartEnd"
                      minTickGap={20}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#546077' }}
                      axisLine={{ stroke: '#e0e1e6' }}
                      tickLine={false}
                      tickFormatter={(v) => `${v}h`}
                    />
                    <Tooltip cursor={{ fill: 'transparent' }} content={<ChartTooltip />} />
                    <Legend
                      iconType="circle"
                      wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                      formatter={(value) => (
                        <span style={{ color: '#0e1e40', marginRight: '8px' }}>{value}</span>
                      )}
                    />
                    <Bar dataKey="Deep Focused" stackId="a" fill="#213D69" />
                    <Bar dataKey="Focused" stackId="a" fill="#5A81BC" />
                    <Bar dataKey="Active" stackId="a" fill="#B5BAC4" />
                    <Bar dataKey="Work Break" stackId="a" fill="#F19235" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Daily Activity */}
            <div className={`rounded-xl bg-white border border-surface-2 ${softShadow} overflow-hidden`}>
              <div className="flex items-center justify-between px-5 py-4">
                <h3 className="text-base font-semibold text-ink-900">Daily Activity</h3>
                <CSVLink data={exportData} headers={csvHeaders} filename="Report.csv">
                  <button
                    type="button"
                    data-tour="report-export"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-rule rounded-[8px] text-xs font-semibold text-ink-900 bg-white hover:border-brand-500 hover:text-brand-700 transition"
                  >
                    <Download size={12} />
                    Export Table
                  </button>
                </CSVLink>
              </div>
              <div className="grid grid-cols-[1fr_180px_180px] items-center px-5 py-2.5 border-b border-surface-1 text-xs text-ink-500 font-medium">
                <span>Project/Policy</span>
                <span className="text-right">Duration</span>
                <span className="text-right pr-2">Engagement Score</span>
              </div>
              <div>
                {tableData.map((day, idx) => (
                  <DayRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={idx}
                    day={day}
                    isOpen={openDays.has(idx)}
                    onToggle={() => toggleDay(idx)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TimeWorked;
